import {
  TICKET_STATUS_KEYWORD,
  type TAccountingCommandResponse,
} from 'features/accounting/types';
import { QueryTags } from 'lib/rtkQuery/constants';

/** Base path of the accounting service behind the API gateway. */
export const AccountingEndpoint = '/accounting';

/** Base path of the customer care service (shift closing tickets) behind the API gateway. */
export const CusCareEndpoint = '/cuscare';

/** Base path of the activity log service ("Lịch sử thao tác") behind the API gateway. */
export const ActivityLogEndpoint = '/activity-log';

/** `metadata.code` of a successful command (the old app's `Result.Success` also accepts HTTP 200). */
export const AccountingSuccessCode = 200;

/** Date format of the accounting service filters (`from`, `to`, ...). */
export const AccountingDateParamFormat = 'YYYY-MM-DD';

/** Date-time format of the accounting service create / update payloads (no offset, no milliseconds). */
export const AccountingDateTimePayloadFormat = 'YYYY-MM-DDTHH:mm:ss';

/** Display formats used by the accounting tables. */
export const AccountingDateDisplayFormat = 'DD-MM-YYYY';
export const AccountingDateTimeDisplayFormat = 'DD-MM-YYYY HH:mm';

/** Date string formats accepted from filter forms / filter tags. */
export const AccountingInputDateFormats = [
  'DD-MM-YYYY',
  'DD/MM/YYYY',
  'YYYY-MM-DD',
  'DD-MM-YYYY HH:mm',
  'YYYY-MM-DDTHH:mm:ss',
];

/** Largest page size accepted by the accounting service (bigger values are clamped server-side). */
export const AccountingMaxPageSize = 200;

export const TicketStatusTagConfig: Record<
  TICKET_STATUS_KEYWORD,
  { label: string; color: string }
> = {
  [TICKET_STATUS_KEYWORD.REFUND]: {
    label: 'Vé hoàn',
    color: 'orange',
  },
  [TICKET_STATUS_KEYWORD.VOID]: {
    label: 'Vé void',
    color: 'red',
  },
  [TICKET_STATUS_KEYWORD.ISSUE_SUCCESS]: {
    label: 'Vé đã xuất',
    color: 'green',
  },
};

/** Partner code owning the company bank accounts (the old app only listed `partnerCode === 'VAA'` accounts). */
export const CompanyBankAccountPartnerCode = 'VAA';

/** Partner code of the company users listed as "Người thực hiện" (the old app only listed `partnerCode === 'vaa'`). */
export const CompanyUserPartnerCode = 'VAA';

/** Gateway path of the customer partner list (sales service). */
export const AccountingPartnerListEndpoint = '/sales/GetAllPartner';

/** Gateway path of the provider list (sales service), loaded in one page like the old app (`length: 1000`). */
export const AccountingProviderListEndpoint = '/sales/ListProvider';
export const AccountingProviderListPageSize = 1000;

/** Minimum keyword length sent to `DepositTransaction` (shorter keywords are sent empty, as in the old app). */
export const AccountingDepositKeywordMinLength = 2;

/** Gateway path of the user list (identity service). */
export const AccountingUserListEndpoint = '/user/get-all-user';

/** Public n8n webhook returning the 787 groups and members (called without the gateway token, as in the old app). */
export const Members787WebhookUrl =
  'https://n8n.787.vn/webhook/787GetMembers-Groups';

/** Cache lifetime (seconds) of the filter lookup lists, which rarely change and are rate limited by the gateway. */
export const AccountingLookupCacheSeconds = 600;

/** Pagination keys of `useQueryHandle` (excluded when rebuilding the URL filters). */
export const AccountingPaginationQueryKeys = ['pageIndex', 'pageSize'];

/** Debounce of the accounting search inputs before the keyword is written to the URL. */
export const AccountingSearchDebounceMs = 300;

/** n8n webhook pulling the new tickets from the providers into the customer care service ("Làm mới dữ liệu"). */
export const ShiftClosingSyncWebhookUrl =
  'https://n8n.787.vn/webhook/UpdateTblTicket';

/** n8n webhook pulling the new hotel bookings into the customer care service (hotel "Làm mới dữ liệu"). */
export const HotelShiftClosingSyncWebhookUrl =
  'https://n8n.787.vn/webhook/UpdateTblHotel';

/** n8n webhook reading a booking (passengers, tickets, segments) by PNR ("Lấy dữ liệu tự động"). */
export const RetrieveBookingWebhookUrl =
  'https://n8n.787.vn/webhook/RetriveBooking';

/** Marker of a failed source in the `UpdateTblTicket` response keys (e.g. `errorHST`), as checked by the old app. */
export const ShiftClosingSyncErrorKeyMarker = 'error';

/** Error status returned by the `UpdateTblTicket` call when the webhook answer is not a list ("Dữ liệu trả về không hợp lệ"). */
export const ShiftClosingSyncInvalidResponseStatus = 'PARSING_ERROR';

/** RTK Query error status of a request that could not be sent (network error), as `fetchBaseQuery` reports it. */
export const AccountingFetchErrorStatus = 'FETCH_ERROR';

/**
 * Bank statement import of the accounting service. It has no API gateway route: like the old app, it is called on the
 * service host directly, without the gateway token (the service allows any origin). Both the dev and the product
 * gateways route every `/accounting` call to this same host, so it does not depend on the environment.
 */
export const ImportBankTransactionUrl =
  'https://accounting.787.vn/api/accounting/ImportBankTransaction';

/** Date format of the reconciliation commands (`issueDate`, `issueTime`, `from`, `to`), parsed strictly by the service. */
export const AccountingReconcileDateFormat = 'DD-MM-YYYY';

/**
 * Shared RTK Query cache keys (`fixedCacheKey`) of the comparison results: written by the import step
 * ("Import dữ liệu") and read by the reconciliation step ("Đối soát") of "Xử lý dữ liệu".
 */
export const ProviderDataCompareCacheKey = 'accounting-provider-data-compare';
export const BankDataCompareCacheKey = 'accounting-bank-data-compare';

/** Bank codes whose statement layout the bank statement import can read. */
export const BankStatementImportBankCodes = ['TCB', 'MB', 'VCB'];

/** Successful command answer used when a `200` body carries no JSON envelope (the request itself succeeded). */
export const EmptyAccountingCommandResponse: TAccountingCommandResponse =
  Object.freeze({
    metadata: null,
    results: null,
  });

/** Host serving the uploaded group files (`groupFile` is a path on the customer care service), as in the old app. */
export const ShiftClosingGroupFileHost = 'https://cuscare.787.vn';

/**
 * Data that changes with the shift closing tickets of the pending data: their list, ticket detail, activity logs
 * and confirmation history.
 */
export const AccountingShiftClosingTags = [
  QueryTags.AccountingShiftClosingList,
  QueryTags.AccountingShiftClosingTicket,
  QueryTags.AccountingHotelShiftClosingList,
  QueryTags.AccountingHotelShiftClosingTicket,
  QueryTags.AccountingActivityLog,
  QueryTags.AccountingActivityLogApproval,
];

/** Data that changes when sales report tickets are unlocked (list, ticket detail and its activity logs). */
export const AccountingSalesReportTicketTags = [
  QueryTags.AccountingSalesReport,
  QueryTags.AccountingSalesReportTicket,
  QueryTags.AccountingActivityLog,
];

/** Lists / reports that change when deposit transactions change. */
export const AccountingDepositTransactionTags = [
  QueryTags.AccountingDepositTransactionList,
  QueryTags.AccountingPayableReport,
  QueryTags.AccountingProviderPayableReport,
  QueryTags.AccountingCustomerPayableSummary,
  QueryTags.AccountingProviderPayableSummary,
];

/**
 * Data that changes with the opening balances: their list, their activity logs and the liabilities reports /
 * summaries, which start from the opening balance.
 */
export const AccountingOpeningBalanceTags = [
  QueryTags.AccountingOpeningBalanceList,
  QueryTags.AccountingActivityLog,
  QueryTags.AccountingPayableReport,
  QueryTags.AccountingProviderPayableReport,
  QueryTags.AccountingCustomerPayableSummary,
  QueryTags.AccountingProviderPayableSummary,
];

/** Date format of the opening balance create / update payloads (the service parses `dd-MM-yyyy`). */
export const AccountingOpeningBalanceDatePayloadFormat = 'DD-MM-YYYY';

/**
 * Guard on the number of pages (of `AccountingMaxPageSize` rows) loaded when every opening balance is needed
 * (browser side filters and Excel export). There is at most one opening balance per partner.
 */
export const AccountingOpeningBalanceMaxPages = 100;

/** Days before today of the payable summaries' default period (the old app opened them on yesterday). */
export const AccountingPayableSummaryDefaultDaysAgo = 1;

/** Guard on the number of pages (of `AccountingMaxPageSize` rows) loaded by a payable summary Excel export. */
export const AccountingPayableSummaryExportMaxPages = 100;

/**
 * Activity log entity of a deposit transaction (`GET /activity-log/get-by-entity`): the accounting service logs
 * CREATE / UPDATE / DELETE per id, but a batch APPROVE (lock) / REJECT (unlock) only under the first id of the batch.
 */
export const DepositTransactionActivityLogServiceName = 'AccountingService';
export const DepositTransactionActivityLogEntityName = 'DepositTransaction';

/** Excel exports built in the browser: file name timestamp, extension and amount number format. */
export const AccountingExportFileDateFormat = 'YYYYMMDDHHmmss';
export const AccountingExportFileExtension = 'xlsx';
export const AccountingExportAmountFormat = '#,##0';
